import type { ReactNode } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { createNextState } from '@reduxjs/toolkit'

import type { Tag as TagType } from '../../graphqlTypes'
import { rpc } from '~publish/legacy/utils/call-rpc/index'

export type ExtraColumn = {
  key: string
  header: string
  width?: string
  renderCell: (data: Post) => ReactNode
}

export type Post = {
  profile_ids: string[]
  text?: string
  media?: { photo?: string; alt_text?: string }
  tags: TagType[]
  created_source: 'bulk_upload'
  top?: boolean
  scheduled_at?: number
  board?: { id: string; name: string }
  postingTime: string
  title?: string
  alt_text?: string
  source_url?: string
}

export type ListItem = {
  id: string
  status: 'valid' | 'loading' | 'success' | 'error'
  errors?: string[]
  data: Post
}

export type CreatePostResponse = {
  success: boolean
  message?: string
  code?: number
}

export const isError = (item: ListItem): boolean => item.status === 'error'
export const isSuccess = (item: ListItem): boolean => item.status === 'success'
export const isValid = (item: ListItem): boolean => item.status === 'valid'

export function isCreatePostResponse(obj: unknown): obj is CreatePostResponse {
  if (
    typeof obj === 'object' &&
    obj !== null &&
    'success' in obj &&
    typeof obj.success === 'boolean'
  ) {
    return true
  }
  return false
}

type CreatePostArgs = Omit<Post, 'postingTime'> & {
  is_draft?: boolean
  subprofile_ids?: string[]
}

export const createPost = async (
  post: CreatePostArgs,
): Promise<CreatePostResponse> => {
  const response = await rpc.call('composerApiProxy', {
    url: '/1/updates/create.json',
    args: post,
    HTTPMethod: 'POST',
  })
  if (isCreatePostResponse(response)) {
    return response
  }
  throw new Error('Invalid response from createPost')
}

export const updateItemStatus = (
  items: ListItem[],
  id: string,
  status: ListItem['status'],
  errors?: string[],
): ListItem[] => {
  return createNextState(items, (draft) => {
    const item = draft.find((candidateItem) => candidateItem.id === id)
    if (item) {
      item.status = status
      if (errors) item.errors = errors
    }
    return draft
  })
}

export const parsePostingTime = (
  postingTime: string,
  channelTimezone: string,
): { top?: boolean; scheduled_at?: number } => {
  if (postingTime === 'Prioritize') {
    return { top: true }
  }

  if (postingTime === 'Next Available' || !postingTime) {
    return {}
  }

  const isoString = postingTime.replace(' ', 'T')
  const plainDateTime = Temporal.PlainDateTime.from(isoString)
  const zonedDateTime = plainDateTime.toZonedDateTime(channelTimezone)

  return { scheduled_at: Math.floor(zonedDateTime.epochMilliseconds / 1000) }
}
